/**
 * VirtualDragLayer - Custom drag layer for virtualized calendar
 * Provides smooth drag preview for shifts across virtual boundaries
 * Also handles placing mode preview (quick copy feature)
 */

import React, { useState, useEffect, RefObject, useCallback, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { format, differenceInMinutes } from 'date-fns';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ROW_HEIGHT_PX } from '@/components/shifts/constants';
import { useShiftUI } from '@/components/shifts/context/ShiftUIProvider';
import { useSetShiftHover } from '@/components/shifts/context/ShiftHoverProvider';

interface DraggedItem {
  shiftId: string;
  title?: string;
  startTime: Date;
  endTime: Date;
  employeeName?: string | null;
  duration: number;
  pixelWidth?: number;
}

interface VirtualDragLayerProps {
  scrollContainerRef?: RefObject<HTMLDivElement>;
  hideDayColumn?: boolean;
  totalDisplayHours?: number;
  hourCellWidthPx?: number;
}

const VirtualDragLayer: React.FC<VirtualDragLayerProps> = ({
  scrollContainerRef,
  hideDayColumn = false,
  totalDisplayHours = 24,
  hourCellWidthPx = 14,
}) => {
  const { isDragging, item, currentOffset, itemType } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as DraggedItem | null,
    currentOffset: monitor.getClientOffset(),
    itemType: monitor.getItemType(),
  }));

  // Get placing mode state from context
  const { isPlacingMode, placingShift } = useShiftUI();

  // Use isolated hover context for time interval highlighting (prevents mass re-renders)
  const setHoveredTimeIntervalIndex = useSetShiftHover();

  // Track mouse position for placing mode preview
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  // Track which scroll zone is active for UI indicators
  const [activeScrollZone, setActiveScrollZone] = useState<'top' | 'bottom' | null>(null);
  const [scrollZoneRect, setScrollZoneRect] = useState<DOMRect | null>(null);

  // PERFORMANCE: Use refs to track last values and avoid redundant updates
  const lastIntervalIndexRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);

  // Calculate interval index from cursor X position
  const calculateIntervalIndex = useCallback((cursorX: number): number | null => {
    if (!scrollContainerRef?.current) return null;

    const calendarRect = scrollContainerRef.current.getBoundingClientRect();
    const scrollLeft = scrollContainerRef.current.scrollLeft;

    // Calculate relative X position within the scrollable content
    const relativeX = cursorX - calendarRect.left + scrollLeft;

    // Account for day column width if present
    const dayColumnWidth = hideDayColumn ? 0 : 32;
    const timeAreaX = relativeX - dayColumnWidth;

    // Calculate interval index (each interval is hourCellWidthPx wide)
    const intervalIndex = Math.floor(timeAreaX / hourCellWidthPx);

    // Clamp to valid range (4 intervals per hour)
    const maxIntervals = Math.ceil(totalDisplayHours * 4);
    if (intervalIndex < 0 || intervalIndex >= maxIntervals) return null;

    return intervalIndex;
  }, [scrollContainerRef, hideDayColumn, hourCellWidthPx, totalDisplayHours]);

  // PERFORMANCE: Only update hover state if interval actually changed
  const updateHoverIfChanged = useCallback((newIndex: number | null) => {
    if (newIndex !== lastIntervalIndexRef.current) {
      lastIntervalIndexRef.current = newIndex;
      setHoveredTimeIntervalIndex(newIndex);
    }
  }, [setHoveredTimeIntervalIndex]);

  // Update hovered interval during drag
  useEffect(() => {
    if (isDragging && currentOffset && scrollContainerRef?.current) {
      const intervalIndex = calculateIntervalIndex(currentOffset.x);
      updateHoverIfChanged(intervalIndex);
    } else if (!isDragging && !isPlacingMode) {
      // Clear when drag ends and not in placing mode
      updateHoverIfChanged(null);
    }
  }, [isDragging, currentOffset?.x, isPlacingMode, calculateIntervalIndex, updateHoverIfChanged]);

  // Auto-scroll when dragging near container edges
  useEffect(() => {
    if (!isDragging || !currentOffset) {
      // Cancel any pending auto-scroll when drag ends
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      // Clear scroll zone indicator
      setActiveScrollZone(null);
      setScrollZoneRect(null);
      return;
    }

    const EDGE_THRESHOLD = 100; // Distance from edge to start scrolling (px)
    const MIN_SCROLL_SPEED = 1; // Minimum scroll speed for smoothness
    const MAX_SCROLL_SPEED = 8; // Maximum scroll speed (reduced for smoothness)

    const performAutoScroll = () => {
      // Find the actual scrollable container - react-window creates this element
      const virtualizedList = document.querySelector('.virtualized-calendar-list') as HTMLElement;
      if (!virtualizedList || !currentOffset) {
        autoScrollRafRef.current = requestAnimationFrame(performAutoScroll);
        return;
      }

      const rect = virtualizedList.getBoundingClientRect();
      const { x: cursorX, y: cursorY } = currentOffset;

      let scrollY = 0;
      let newZone: 'top' | 'bottom' | null = null;

      // Vertical scrolling (top/bottom edges) - this is the main scroll direction
      const distanceFromTop = cursorY - rect.top;
      const distanceFromBottom = rect.bottom - cursorY;

      if (distanceFromTop < EDGE_THRESHOLD && distanceFromTop > 0) {
        // Scroll up - smooth cubic easing for natural feel
        const intensity = 1 - (distanceFromTop / EDGE_THRESHOLD);
        const smoothIntensity = intensity * intensity * intensity; // Cubic for very smooth start
        scrollY = -(MIN_SCROLL_SPEED + Math.round((MAX_SCROLL_SPEED - MIN_SCROLL_SPEED) * smoothIntensity));
        newZone = 'top';
      } else if (distanceFromBottom < EDGE_THRESHOLD && distanceFromBottom > 0) {
        // Scroll down
        const intensity = 1 - (distanceFromBottom / EDGE_THRESHOLD);
        const smoothIntensity = intensity * intensity * intensity;
        scrollY = MIN_SCROLL_SPEED + Math.round((MAX_SCROLL_SPEED - MIN_SCROLL_SPEED) * smoothIntensity);
        newZone = 'bottom';
      }

      // Update scroll zone indicator
      setActiveScrollZone(newZone);
      setScrollZoneRect(newZone ? rect : null);

      // Apply vertical scroll to the virtualized list
      if (scrollY !== 0) {
        virtualizedList.scrollTop += scrollY; // Direct assignment for smoother scrolling
      }

      // Continue auto-scroll loop while dragging
      autoScrollRafRef.current = requestAnimationFrame(performAutoScroll);
    };

    // Start auto-scroll loop
    autoScrollRafRef.current = requestAnimationFrame(performAutoScroll);

    return () => {
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };
  }, [isDragging, currentOffset]);

  useEffect(() => {
    if (!isPlacingMode) {
      setMousePosition(null);
      return;
    }

    const EDGE_THRESHOLD = 100;
    const MIN_SCROLL_SPEED = 1;
    const MAX_SCROLL_SPEED = 8;

    // PERFORMANCE: Throttle mouse move updates using requestAnimationFrame
    const handleMouseMove = (e: MouseEvent) => {
      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Schedule update for next frame
      rafIdRef.current = requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });

        // Update hovered interval during placing mode (only if changed)
        if (scrollContainerRef?.current) {
          const intervalIndex = calculateIntervalIndex(e.clientX);
          updateHoverIfChanged(intervalIndex);
        }

        // Auto-scroll during placing mode - use the virtualized list container
        const virtualizedList = document.querySelector('.virtualized-calendar-list') as HTMLElement;
        if (virtualizedList) {
          const rect = virtualizedList.getBoundingClientRect();
          let scrollY = 0;

          const distanceFromTop = e.clientY - rect.top;
          const distanceFromBottom = rect.bottom - e.clientY;

          if (distanceFromTop < EDGE_THRESHOLD && distanceFromTop > 0) {
            const intensity = 1 - (distanceFromTop / EDGE_THRESHOLD);
            const smoothIntensity = intensity * intensity * intensity;
            scrollY = -(MIN_SCROLL_SPEED + Math.round((MAX_SCROLL_SPEED - MIN_SCROLL_SPEED) * smoothIntensity));
          } else if (distanceFromBottom < EDGE_THRESHOLD && distanceFromBottom > 0) {
            const intensity = 1 - (distanceFromBottom / EDGE_THRESHOLD);
            const smoothIntensity = intensity * intensity * intensity;
            scrollY = MIN_SCROLL_SPEED + Math.round((MAX_SCROLL_SPEED - MIN_SCROLL_SPEED) * smoothIntensity);
          }

          if (scrollY !== 0) {
            virtualizedList.scrollTop += scrollY;
          }
        }
        rafIdRef.current = null;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      // Clear hovered interval when placing mode ends
      updateHoverIfChanged(null);
    };
  }, [isPlacingMode, scrollContainerRef, calculateIntervalIndex, updateHoverIfChanged]);

  // Render placing mode preview
  if (isPlacingMode && placingShift && mousePosition) {
    // Use the actual pixel width from the source shift
    const previewWidth = placingShift.sourcePixelWidth;

    return (
      <div
        className="placing-mode-layer"
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 9999,
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <div
          className="placing-preview"
          style={{
            position: 'absolute',
            left: mousePosition.x,
            top: mousePosition.y,
            transform: 'translateY(-50%)', // Only center vertically, anchor left edge to cursor
            width: `${previewWidth}px`,
            height: `${ROW_HEIGHT_PX - 6}px`,
            opacity: 0.85,
          }}
        >
          {/* Placing Preview Card */}
          <div
            className="shift-placing-preview px-2 py-1 rounded-md bg-green-600 text-white shadow-2xl border-2 border-green-400 flex flex-col justify-center"
            style={{
              height: '100%',
              cursor: 'crosshair',
            }}
          >
            {/* Shift Title */}
            <div className="text-xs font-medium truncate">
              {placingShift.title}
            </div>

            {/* Duration */}
            <div className="text-xxs opacity-90">
              {Math.floor(placingShift.duration / 60)}h {placingShift.duration % 60 > 0 ? `${placingShift.duration % 60}m` : ''}
            </div>

            {/* Click to place hint */}
            <div className="text-xxs opacity-75 mt-0.5">
              Click to place · ESC to cancel
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Only render for shift drags
  if (!isDragging || itemType !== 'PLACED_SHIFT' || !item || !currentOffset) {
    return null;
  }

  // Calculate shift duration for display
  const durationMinutes = item.duration || differenceInMinutes(item.endTime, item.startTime);
  const durationHours = durationMinutes / 60;

  // Use actual pixel width from the shift
  const previewWidth = item.pixelWidth || Math.max(100, durationHours * 40);

  return (
    <div
      className="virtual-drag-layer"
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 9999,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      }}
    >
      {/* Scroll Zone Indicators */}
      {activeScrollZone && scrollZoneRect && (
        <>
          {activeScrollZone === 'top' && (
            <div
              className="scroll-zone-indicator-top bg-gradient-to-b from-primary/20 to-transparent"
              style={{
                position: 'absolute',
                left: scrollZoneRect.left,
                top: scrollZoneRect.top,
                width: scrollZoneRect.width,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <div className="flex items-center gap-2 bg-primary/90 text-primary-foreground px-3 py-1.5 rounded-full shadow-lg">
                <ChevronUp className="w-4 h-4 animate-bounce" />
                <span className="text-xs font-medium">Scrolling up</span>
                <ChevronUp className="w-4 h-4 animate-bounce" />
              </div>
            </div>
          )}
          {activeScrollZone === 'bottom' && (
            <div
              className="scroll-zone-indicator-bottom bg-gradient-to-t from-primary/20 to-transparent"
              style={{
                position: 'absolute',
                left: scrollZoneRect.left,
                bottom: window.innerHeight - scrollZoneRect.bottom,
                width: scrollZoneRect.width,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <div className="flex items-center gap-2 bg-primary/90 text-primary-foreground px-3 py-1.5 rounded-full shadow-lg">
                <ChevronDown className="w-4 h-4 animate-bounce" />
                <span className="text-xs font-medium">Scrolling down</span>
                <ChevronDown className="w-4 h-4 animate-bounce" />
              </div>
            </div>
          )}
        </>
      )}

      <div
        className="drag-preview"
        style={{
          position: 'absolute',
          left: currentOffset.x,
          top: currentOffset.y,
          transform: 'translateY(-50%)', // Only center vertically, anchor left edge to cursor
          width: `${previewWidth}px`,
          height: `${ROW_HEIGHT_PX - 6}px`, // Slightly smaller than actual
          opacity: 0.8,
          transition: 'none', // No transitions during drag for smooth movement
        }}
      >
        {/* Shift Preview Card */}
        <div
          className="shift-drag-preview px-2 py-1 rounded-md bg-blue-500/60 dark:bg-blue-400/60 text-white shadow-2xl flex flex-col justify-center outline-none ring-0 border-0"
          style={{
            height: '100%',
            cursor: 'grabbing',
          }}
        >
          {/* Shift Title */}
          <div className="text-xs font-medium truncate">
            {item.title || 'Shift'}
          </div>

          {/* Time Range */}
          <div className="text-xxs opacity-90 truncate">
            {format(item.startTime, 'HH:mm')} - {format(item.endTime, 'HH:mm')}
          </div>

          {/* Employee Name (if assigned) */}
          {item.employeeName && (
            <div className="text-xxs opacity-75 truncate">
              {item.employeeName}
            </div>
          )}

          {/* Duration Indicator */}
          <div className="text-xxs opacity-75 mt-0.5">
            {durationHours.toFixed(1)}h
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualDragLayer;